// oxlint-disable-next-line no-restricted-imports -- hydration-safe: see ARCHITECTURE.md trap #1
import { useMediaQuery } from 'react-responsive'

import { useHasMounted } from './useHasMounted'

type Query = Parameters<typeof useMediaQuery>[0]

/**
 * Hydration-safe viewport check.
 *
 * `react-responsive`'s `useMediaQuery` reads `matchMedia`, which is unavailable
 * during SSR. In this statically-exported app the server renders the *desktop*
 * branch; on the client the hook resolves to the real (mobile) value on the
 * very first render, so the server/client trees disagree. React logs a
 * hydration mismatch and — critically — refuses to patch up the mismatched
 * attributes (e.g. an <img src>), leaving mobile stuck on the desktop asset.
 *
 * Gating behind `useHasMounted` makes the first client render match the server
 * (returns the SSR fallback), then flips to the real value after mount via a
 * normal re-render — which React applies correctly.
 *
 * @param query   media query object (default: phones, max-width 767px)
 * @param ssrValue value to return during SSR + first client render (default false)
 */
export function useIsMobile(query: Query = { maxWidth: 767 }, ssrValue = false) {
  const hasMounted = useHasMounted()
  const matches = useMediaQuery(query)
  return hasMounted ? matches : ssrValue
}
